import { readCookie } from "./cookie";

export type JwtPayload = {
  sub: string; // User ID
  exp: number; // epoch time in seconds
  iat: number; // epoch time in seconds
  iss: string;
} & Record<string, unknown>;

const BASE64_LOOKUP: Record<string, number> = {};
"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/".split("").forEach((char, i) => {
  BASE64_LOOKUP[char] = i;
});

const decodeBase64Url = (input: string): string => {
  const str = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = str.length % 4;
  const padded = pad ? str + "=".repeat(4 - pad) : str;

  let output = "";
  for (let i = 0; i < padded.length; i += 4) {
    const a = BASE64_LOOKUP[padded[i]!]!;
    const b = BASE64_LOOKUP[padded[i + 1]!]!;
    const c = BASE64_LOOKUP[padded[i + 2]!];
    const d = BASE64_LOOKUP[padded[i + 3]!];

    output += String.fromCharCode((a << 2) | (b >> 4));
    if (c !== undefined && padded[i + 2] !== "=") {
      output += String.fromCharCode(((b & 15) << 4) | (c >> 2));
    }
    if (d !== undefined && padded[i + 3] !== "=") {
      output += String.fromCharCode(((c! & 3) << 6) | d);
    }
  }

  return output;
};

const decode = <T extends JwtPayload>(token: string): T => {
  const payloadBase64 = token.split(".")[1];

  if (!payloadBase64) {
    throw new Error("Invalid JWT token");
  }

  const decodedJson = decodeBase64Url(payloadBase64);
  return JSON.parse(decodedJson) as T;
};

const base64UrlToBytes = (input: string): Uint8Array<ArrayBuffer> | undefined => {
  if (!/^[A-Za-z0-9_-]*$/.test(input) || input.length % 4 === 1) {
    return undefined;
  }
  const str = input.replace(/-/g, "+").replace(/_/g, "/");
  const bytes = new Uint8Array(Math.floor((str.length * 3) / 4));
  let bits = 0;
  let buffer = 0;
  let index = 0;
  for (const char of str) {
    buffer = (buffer << 6) | BASE64_LOOKUP[char]!;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes[index++] = (buffer >> bits) & 255;
    }
  }
  return bytes;
};

const parseSegment = (segment: string): Record<string, unknown> | undefined => {
  const bytes = base64UrlToBytes(segment);
  if (!bytes) {
    return undefined;
  }
  const value: unknown = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
};

let cachedKey: { secret: string; key: Promise<CryptoKey> } | undefined;

const verificationKey = (secret: string): Promise<CryptoKey> => {
  if (cachedKey?.secret !== secret) {
    const key = crypto.subtle.importKey("raw", new TextEncoder().encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["verify"]);
    cachedKey = { secret, key };
    key.catch(() => {
      if (cachedKey?.key === key) {
        cachedKey = undefined;
      }
    });
  }
  return cachedKey.key;
};

const verifyHs256 = async <T extends JwtPayload = JwtPayload>(token: string, secret: string, options: { issuer: string }): Promise<T | undefined> => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      return undefined;
    }
    const [headerPart, payloadPart, signaturePart] = parts as [string, string, string];
    if (!headerPart || !payloadPart || !signaturePart) {
      return undefined;
    }
    const header = parseSegment(headerPart);
    const payload = parseSegment(payloadPart);
    const signature = base64UrlToBytes(signaturePart);
    if (!header || !payload || !signature || header.alg !== "HS256") {
      return undefined;
    }
    const key = await verificationKey(secret);
    const valid = await crypto.subtle.verify("HMAC", key, signature, new TextEncoder().encode(`${headerPart}.${payloadPart}`));
    if (!valid || payload.iss !== options.issuer) {
      return undefined;
    }
    const now = Math.floor(Date.now() / 1000);
    if (typeof payload.exp !== "number" || payload.exp <= now) {
      return undefined;
    }
    if (typeof payload.nbf === "number" && payload.nbf > now) {
      return undefined;
    }
    return payload as T;
  } catch {
    return undefined;
  }
};

const bearerToken = (header: string | null | undefined): string | undefined => {
  const match = header?.match(/^\s*Bearer\s+(\S+)\s*$/i);
  return match?.[1];
};

const fromHeaders = (headers: { cookie?: string | null; authorization?: string | null }, cookieName?: string): string | undefined =>
  (cookieName ? readCookie(headers.cookie, cookieName) : undefined) ?? bearerToken(headers.authorization);

export const jwt = {
  decode,
  verifyHs256,
  bearerToken,
  fromHeaders,
};
